/**
 * The sign-in card's glyphs — CAOS-132 originally, rewritten under CAOS-602.
 *
 * This file used to DRAW five glyphs of its own, and its header said why: the shared vocabulary did
 * not expose mail, eye/eye-off or shield. That reason is the exact mechanism a single icon
 * vocabulary exists to prevent — a surface that cannot find the mark it needs draws one, and the
 * second icon set has started. Two of the five (the padlock, the struck-through eye) were marks the
 * vocabulary already carried and this file had re-drawn anyway; the other three are in it now.
 *
 * What remains here is the sign-in card's PRESENTATION of them, which is this surface's decision to
 * make: a lighter 1.8 stroke, because the card draws its glyphs small inside a field, and the
 * decorative attributes a leading field glyph needs. The artwork itself comes from one place.
 */
import { makeIcon, type IconName } from '@caos/ui';

/** One mark from the platform vocabulary, dressed the way the sign-in card draws them. */
function signInGlyph(name: IconName): SVGSVGElement {
  const svg = makeIcon(name);
  svg.setAttribute('stroke-width', '1.8');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  return svg;
}

/** Envelope — the email field's leading glyph. */
export function mailIcon(): SVGSVGElement {
  return signInGlyph('mail');
}

/** Padlock — the password field's leading glyph. */
export function lockIcon(): SVGSVGElement {
  return signInGlyph('lock');
}

/** Eye — reveal-password affordance (open). */
export function eyeIcon(): SVGSVGElement {
  return signInGlyph('eye');
}

/** Eye with a slash — reveal-password affordance (hidden state). */
export function eyeOffIcon(): SVGSVGElement {
  return signInGlyph('eye-off');
}

/** Shield — the SSO button's leading glyph. */
export function shieldIcon(): SVGSVGElement {
  return signInGlyph('shield');
}
